import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Repeat, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function RecurrenceDialog({ training, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [until, setUntil] = useState(() => {
    const d = new Date(training.date); d.setDate(d.getDate() + 28); return d.toISOString().slice(0,10);
  });
  const [mode, setMode] = useState("skip");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const reset = () => { setPreview(null); };

  const doPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/trainings/recurrence", {
        training_id: training.id, until_date: until, preview: true,
      });
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };

  const doSave = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/trainings/recurrence", {
        training_id: training.id, until_date: until, conflict_mode: mode,
      });
      toast.success(`${data.created} sessioni create · ${data.skipped} saltate · ${data.replaced} sostituite`);
      setOpen(false); reset(); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ricorrenza settimanale</DialogTitle>
          <DialogDescription>
            "{training.title}" verrà ripetuto ogni settimana lo stesso giorno fino alla data indicata.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label>Fine ricorrenza</Label>
            <Input data-testid="rec-until" type="date" value={until} min={training.date} onChange={(e) => setUntil(e.target.value)} />
            <div className="text-xs text-zinc-500 mt-1">Dal {training.date} (incluso) fino al {until}</div>
          </div>

          {!preview && (
            <Button data-testid="rec-preview" variant="outline" onClick={doPreview} disabled={loading} className="w-full">
              {loading ? "Verifica…" : "Verifica conflitti"}
            </Button>
          )}

          {preview && (
            <>
              <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 text-sm">
                <div><strong>{preview.count}</strong> sessioni previste</div>
                {preview.conflicts.length > 0 && (
                  <div className="text-amber-700 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {preview.conflicts.length} conflitto/i</div>
                )}
              </div>
              {preview.conflicts.length > 0 && (
                <div className="max-h-32 overflow-y-auto bg-white border border-zinc-200 rounded p-2 space-y-1">
                  {preview.conflicts.map((c) => (
                    <div key={c.id} className="text-xs font-mono flex gap-2">
                      <span className="text-zinc-500">{c.date}</span><span>{c.title}</span>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <Label>In caso di conflitto</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button data-testid="rec-mode-skip" onClick={() => setMode("skip")} className={`p-3 rounded-md border text-sm text-left ${mode === "skip" ? "border-black bg-zinc-50" : "border-zinc-200"}`}>
                    <div className="font-semibold">Salta</div>
                    <div className="text-xs text-zinc-500 mt-1">Non duplicare nei giorni con sessione</div>
                  </button>
                  <button data-testid="rec-mode-overwrite" onClick={() => setMode("overwrite")} className={`p-3 rounded-md border text-sm text-left ${mode === "overwrite" ? "border-red-600 bg-red-50" : "border-zinc-200"}`}>
                    <div className="font-semibold text-red-700">Sovrascrivi</div>
                    <div className="text-xs text-zinc-500 mt-1">Sostituisci le sessioni esistenti</div>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          {preview && (
            <Button data-testid="rec-save" onClick={doSave} disabled={loading} className={mode === "overwrite" ? "bg-red-600 hover:bg-red-700" : "bg-black hover:bg-zinc-800"}>
              {loading ? "Creazione…" : (mode === "overwrite" ? "Crea e sovrascrivi" : "Crea ricorrenza")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
